import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Loader2, Star, Target } from "lucide-react";
import { api } from "../../lib/api";
import { apiErrorMessage } from "../../lib/errors";
import SportTabs from "../../components/terminal/SportTabs";
import {
  Cell,
  EmptyState,
  HeadCell,
  MatchupCell,
  StartTimeCell,
  dqTone,
  edgeToneClass,
  fmtAmerican,
  fmtPct,
} from "../../components/terminal/DataTable";

function Stars({ n }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={10}
          strokeWidth={1.5}
          className={i <= n ? "fill-[#00D4FF] text-[#00D4FF]" : "text-zinc-700"}
        />
      ))}
    </div>
  );
}

function SimRangeBar({ home, low, high }) {
  const h = Number(home) || 0;
  const l = Number(low) || 0;
  const hi = Number(high) || 0;
  if (!hi) return <span className="text-zinc-600">—</span>;
  return (
    <div className="w-28">
      <div className="mono text-[10px] text-zinc-400 mb-0.5">
        {l.toFixed(0)}–{hi.toFixed(0)}%
      </div>
      <div className="relative h-1.5 bg-zinc-900 border border-zinc-800">
        <div
          className="absolute top-0 h-full bg-[#00D4FF]/40"
          style={{ left: `${l}%`, width: `${Math.max(0, hi - l)}%` }}
        />
        <div
          className="absolute top-[-2px] h-[10px] w-[2px] bg-[#00D4FF]"
          style={{ left: `${h}%` }}
          title={`Model: ${h.toFixed(1)}%`}
        />
      </div>
    </div>
  );
}

export default function BestEdge() {
  const [rows, setRows] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [sport, setSport] = useState("");
  const [locking, setLocking] = useState("");

  const load = useCallback(async (s) => {
    try {
      const params = s ? { sport: s } : {};
      const { data } = await api.get("/terminal/best-edge", { params });
      setRows(data.rows || []);
    } catch (err) {
      console.warn("best-edge load failed", err);
      toast.error(apiErrorMessage(err, "Could not load best edge"));
      setRows([]);
    }
  }, []);

  useEffect(() => {
    load(sport);
  }, [sport, load]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await api.post("/terminal/refresh");
      toast.success("Fresh slate evaluated");
      await load(sport);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) toast.info(apiErrorMessage(err, "Slow down — try in a minute"));
      else toast.error(apiErrorMessage(err, "Refresh failed"));
    } finally {
      setRefreshing(false);
    }
  }, [load, sport]);

  const lockAsStep1 = useCallback(async (r) => {
    const leanIsHome = r.lean && r.home_name && r.lean.toLowerCase().includes(r.home_name.toLowerCase());
    const dkAm = leanIsHome ? r.dk_home : r.dk_away;
    setLocking(r.key);
    try {
      await api.post("/bets", {
        sport: r.sport,
        matchup: r.matchup,
        pick: r.lean,
        stake: 10,
        american_odds: Number.isFinite(Number(dkAm)) ? Math.round(Number(dkAm)) : -110,
        result: "pending",
        step: 1,
        variant: "classic",
        notes: `Locked from Terminal · edge ${r.edge_pct}% · DQ ${r.data_quality_score}`,
      });
      toast.success(`Step 1 locked · ${r.lean}`);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not lock bet"));
    } finally {
      setLocking("");
    }
  }, []);

  return (
    <div data-testid="best-edge-page">
      <div className="flex items-start justify-between mb-4 gap-4">
        <div>
          <h2 className="font-display text-2xl">Best edge · ranked by model +EV</h2>
          <p className="mono text-[11px] text-zinc-500 mt-1">
            Only games the AI flagged as Martingale step-1 viable. Ranked by (model
            win % − market implied %). Click LOCK to send step-1 to your tracker.
          </p>
        </div>
        <button
          onClick={onRefresh}
          disabled={refreshing}
          data-testid="best-edge-refresh"
          className="mono text-[11px] text-zinc-400 hover:text-[#00D4FF] flex items-center gap-1.5 disabled:opacity-50"
        >
          {refreshing ? (
            <Loader2 size={12} className="animate-spin" />
          ) : (
            <RefreshCw size={12} strokeWidth={1.5} />
          )}
          REFRESH SLATE
        </button>
      </div>

      <SportTabs value={sport} onChange={setSport} testidPrefix="best-edge-sport" />

      {rows === null && (
        <div className="py-8 text-center mono text-xs text-zinc-500">
          <Loader2 size={16} className="inline animate-spin mr-2" />
          Loading tape…
        </div>
      )}
      {rows && rows.length === 0 && (
        <EmptyState
          testid="best-edge-empty"
          message="No +EV plays on the evaluated slate. Hit REFRESH SLATE to pre-evaluate the next 10 closest-to-pick'em games."
        />
      )}

      {rows && rows.length > 0 && (
        <div className="border border-zinc-800 overflow-x-auto" data-testid="best-edge-table">
          <table className="w-full min-w-[1000px]">
            <thead>
              <tr>
                <HeadCell>START</HeadCell>
                <HeadCell>MATCHUP</HeadCell>
                <HeadCell>LEAN</HeadCell>
                <HeadCell className="text-right">CONF</HeadCell>
                <HeadCell className="text-right">MODEL %</HeadCell>
                <HeadCell>SIM RANGE</HeadCell>
                <HeadCell className="text-right">FAIR</HeadCell>
                <HeadCell className="text-right">DK</HeadCell>
                <HeadCell className="text-right">EDGE</HeadCell>
                <HeadCell className="text-right">DQ</HeadCell>
                <HeadCell className="text-center">LOCK</HeadCell>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const leanIsHome =
                  r.lean && r.home_name && r.lean.toLowerCase().includes(r.home_name.toLowerCase());
                const modelPct = leanIsHome ? r.sim_model_home_pct : r.sim_model_away_pct;
                const simLow = leanIsHome ? r.sim_home_low : r.sim_away_low;
                const simHigh = leanIsHome ? r.sim_home_high : r.sim_away_high;
                const fairAm = leanIsHome ? r.fair_home : r.fair_away;
                const dkAm = leanIsHome ? r.dk_home : r.dk_away;
                return (
                  <tr
                    key={r.key}
                    className="hover:bg-zinc-900/40"
                    data-testid={`best-edge-row-${r.key}`}
                  >
                    <Cell><StartTimeCell iso={r.start_time} /></Cell>
                    <Cell><MatchupCell row={r} /></Cell>
                    <Cell className="text-[#00D4FF]">{r.lean}</Cell>
                    <Cell className="text-right"><Stars n={r.confidence || 0} /></Cell>
                    <Cell className="text-right">{modelPct?.toFixed(1) ?? "—"}%</Cell>
                    <Cell><SimRangeBar home={modelPct} low={simLow} high={simHigh} /></Cell>
                    <Cell className="text-right text-[#00D4FF]">{fmtAmerican(fairAm)}</Cell>
                    <Cell className="text-right">{fmtAmerican(dkAm)}</Cell>
                    <Cell className={`text-right font-bold ${edgeToneClass(r.edge_pct, r.edge_label)}`}>
                      {fmtPct(r.edge_pct)}
                    </Cell>
                    <Cell className={`text-right ${dqTone(r.data_quality_score)}`}>
                      {r.data_quality_score ?? 0}
                    </Cell>
                    <Cell className="text-center">
                      <button
                        onClick={() => lockAsStep1(r)}
                        disabled={locking === r.key}
                        data-testid={`best-edge-lock-${r.key}`}
                        className="mono text-[10px] px-2 py-1 border border-[#00D4FF] text-[#00D4FF] hover:bg-[#00D4FF]/10 disabled:opacity-40 flex items-center gap-1 mx-auto"
                      >
                        {locking === r.key ? (
                          <Loader2 size={10} className="animate-spin" />
                        ) : (
                          <Target size={10} strokeWidth={2} />
                        )}
                        LOCK
                      </button>
                    </Cell>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
